import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Edit, ArrowLeft, User, Pill, AlertTriangle, Shield, Search, X, AlertCircle, Plus, Minus, Save , RotateCcw} from 'lucide-react';
import patientService from '../../patient/services/patientService';
import { patientMedicalService } from '../../../services/patientMedicalService';

const AdminPatientMedicalDetailsPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  
  // State for patient details
  const [patient, setPatient] = useState(null);
  const [patientAccountsDetails, setPatientAccountsDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // State for search inputs (editable)
  const [searchData, setSearchData] = useState({
    patientId: '',
    patientName: '',
    patientDOB: ''
  });

  // State for patient search and selection
  const [showPatientIdDropdown, setShowPatientIdDropdown] = useState(false);
  const [showPatientNameDropdown, setShowPatientNameDropdown] = useState(false);
  const [filteredPatientsById, setFilteredPatientsById] = useState([]);
  const [filteredPatientsByName, setFilteredPatientsByName] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isPatientSelected, setIsPatientSelected] = useState(false);

  // State for real patient data from API
  const [allPatients, setAllPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // State for toast notifications
  const [toast, setToast] = useState(null);

  // State for collapsible search section (closed by default)
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // State for collapsible detail sections
  const [isGeneralDetailsExpanded, setIsGeneralDetailsExpanded] = useState(true);
  const [isMedicationsExpanded, setIsMedicationsExpanded] = useState(false);
  const [isMedicalAllergiesExpanded, setIsMedicalAllergiesExpanded] = useState(false);
  const [isOtherAllergiesExpanded, setIsOtherAllergiesExpanded] = useState(false);
  
  // State for BMI tooltip
  const [showBMITooltip, setShowBMITooltip] = useState(false);

  // State for editing functionality
  const [editingSection, setEditingSection] = useState(null);
  const [backupData, setBackupData] = useState({});
  
  // Editable data states
  const [editablePatient, setEditablePatient] = useState({
    height: '',
    weight: ''
  });

  // Real medical data from API
  const [medications, setMedications] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [medicalDataLoading, setMedicalDataLoading] = useState(false);

  // Editable medical data arrays (with full record objects)
  const [editableMedications, setEditableMedications] = useState([]);
  const [editableMedicalAllergies, setEditableMedicalAllergies] = useState([]);
  const [editableOtherAllergies, setEditableOtherAllergies] = useState([]);
  
  // Track original data for comparison during save
  const [originalMedications, setOriginalMedications] = useState([]);
  const [originalMedicalAllergies, setOriginalMedicalAllergies] = useState([]);
  const [originalOtherAllergies, setOriginalOtherAllergies] = useState([]);
  
  // Reference data for dropdowns
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [dosageOptions, setDosageOptions] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [allergyTypeOptions, setAllergyTypeOptions] = useState([]);
  const [allergySeverityOptions, setAllergySeverityOptions] = useState([]);

  // Input states for adding new tags
  const [medicationInput, setMedicationInput] = useState('');
  const [medicalAllergyInput, setMedicalAllergyInput] = useState('');
  const [otherAllergyInput, setOtherAllergyInput] = useState('');

  // Set page title and fetch patient data
  useEffect(() => {
    document.title = 'MediFusion - Admin Patient Files';
    fetchPatientData();
    fetchAllPatients();
    loadReferenceData();
  }, [patientId]);

  // Update search fields when patient data is loaded and fetch medical data
  useEffect(() => {
    if (patient) {
      setSearchData({
        patientId: patient.patient_id || '',
        patientName: `${patient.first_name} ${patient.last_name}`,
        patientDOB: patient.dob || ''
      });
      setSelectedPatient(patient);
      setIsPatientSelected(true);
      
      // Fetch medical data when patient is loaded
      fetchMedicalData(patient.id);
    }
  }, [patient]);

  // Fetch patient data from API
  const fetchPatientData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching patient details for ID:', patientId);
      
      // Try to fetch patient by patient_id (string format like P001)
      const patientData = await patientService.getPatientByPatientId(patientId);
      console.log('👤 Patient data from API:', patientData);
      console.log('📧 Patient user_email field:', patientData.user_email);
      console.log('📧 Patient email field:', patientData.email);
      console.log('👤 Patient user object:', patientData.user);
      
      setPatient(patientData);
      
      // Populate editable patient data
      setEditablePatient({
        height: patientData.height || '',
        weight: patientData.weight || ''
      });

      // Fetch patient accounts details if patient has numeric ID
      if (patientData?.id) {
        try {
          console.log('🔍 Attempting to fetch accounts details for patient ID:', patientData.id);
          const accountsData = await patientService.getPatientAccountsDetailsByPatientId(patientData.id);
          console.log('💼 Patient accounts details successfully fetched:', accountsData);
          setPatientAccountsDetails(accountsData);
        } catch (error) {
          console.log('ℹ️ No accounts details found for patient:', error.message);
          setPatientAccountsDetails(null);
        }
      } else {
        console.log('⚠️ Patient data missing numeric ID field for accounts details lookup');
        setPatientAccountsDetails(null);
      }
    } catch (error) {
      console.error('❌ Error fetching patient data:', error);
      setPatient(null);
      setPatientAccountsDetails(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all patients from the database
  const fetchAllPatients = async () => {
    try {
      setLoadingPatients(true);
      console.log('🔍 Fetching all patients from database...');
      const response = await patientService.getAllPatients();
      console.log('👥 Patients from API:', response);
      
      // The response should have a patients array and metadata
      const patients = response.patients || response || [];
      setAllPatients(patients);
      console.log('✅ Loaded', patients.length, 'patients from database');
    } catch (error) {
      console.error('❌ Error fetching patients:', error);
      // Fallback to empty array if API fails
      setAllPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Load reference data for dropdowns
  const loadReferenceData = async () => {
    try {
      const [medications, dosages, frequencies, allergyTypes, allergySeverities] = await Promise.all([
        patientMedicalService.getMedicationOptions(),
        patientMedicalService.getDosageOptions(), 
        patientMedicalService.getFrequencyOptions(),
        patientMedicalService.getAllergyTypeOptions(),
        patientMedicalService.getAllergySeverityOptions()
      ]);

      setMedicationOptions(medications);
      setDosageOptions(dosages);
      setFrequencyOptions(frequencies);
      setAllergyTypeOptions(allergyTypes);
      setAllergySeverityOptions(allergySeverities);

    } catch (error) {
      console.error('❌ Error loading reference data:', error);
      // Set fallback options
      setMedicationOptions(['Lisinopril', 'Metformin', 'Atorvastatin', 'Aspirin']);
      setDosageOptions(['10mg', '20mg', '50mg', '100mg', '500mg']);
      setFrequencyOptions(['Once daily', 'Twice daily', 'Three times daily']);
      setAllergyTypeOptions(['Drug', 'Food', 'Environmental', 'Other']);
      setAllergySeverityOptions(['Mild', 'Moderate', 'Severe', 'Life-threatening']);
    }
  };

  // Fetch medical data from API
  const fetchMedicalData = async (patientId) => {
    try {
      setMedicalDataLoading(true);
      console.log('🔍 Fetching medical data for patient ID:', patientId);

      // Fetch only ACTIVE medications and allergies for UI display
      const [medicationsData, allergiesData] = await Promise.all([
        patientMedicalService.getActiveMedications(patientId),
        patientMedicalService.getActiveAllergies(patientId)
      ]);

      console.log('💊 Medications data:', medicationsData);
      console.log('🚨 Allergies data:', allergiesData);

      setMedications(medicationsData);
      setAllergies(allergiesData);

      // Store original data for comparison during save
      setOriginalMedications([...medicationsData]);
      
      const drugAllergies = allergiesData.filter(allergy => allergy.allergy_type === 'Drug');
      const otherAllergies = allergiesData.filter(allergy => allergy.allergy_type !== 'Drug');
      
      setOriginalMedicalAllergies([...drugAllergies]);
      setOriginalOtherAllergies([...otherAllergies]);

      // Initialize editable arrays with real data (keep as objects for proper tracking)
      setEditableMedications([...medicationsData]);
      setEditableMedicalAllergies([...drugAllergies]);
      setEditableOtherAllergies([...otherAllergies]);

    } catch (error) {
      console.error('❌ Error fetching medical data:', error);
      console.log('🔄 Falling back to empty data');
      
      // Clear all data if API fails
      setMedications([]);
      setAllergies([]);
      setOriginalMedications([]);
      setOriginalMedicalAllergies([]);
      setOriginalOtherAllergies([]);
      setEditableMedications([]);
      setEditableMedicalAllergies([]);
      setEditableOtherAllergies([]);
    } finally {
      setMedicalDataLoading(false);
    }
  };

  // Filter patients by ID
  const filterPatientsById = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredPatientsById([]);
      return;
    }

    const filtered = allPatients.filter(patient => 
      patient.patient_id && patient.patient_id.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPatientsById(filtered);
  };

  // Filter patients by name
  const filterPatientsByName = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredPatientsByName([]);
      return;
    }

    const filtered = allPatients.filter(patient => 
      `${patient.first_name} ${patient.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPatientsByName(filtered);
  };

  // Handle patient ID input change
  const handlePatientIdChange = (value) => {
    setSearchData(prev => ({ ...prev, patientId: value }));
    filterPatientsById(value);
    if (value) {
      setShowPatientIdDropdown(true);
      setShowPatientNameDropdown(false);
    }
  };

  // Handle patient name input change
  const handlePatientNameChange = (value) => {
    setSearchData(prev => ({ ...prev, patientName: value }));
    filterPatientsByName(value);
    if (value) {
      setShowPatientNameDropdown(true);
      setShowPatientIdDropdown(false);
    }
  };

  // Handle patient selection from dropdown
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    
    // Format DOB for the date input field
    let formattedDOB = '';
    if (patient.dob && patient.dob !== '1900-01-01' && patient.dob !== null) {
      formattedDOB = patient.dob; // API already returns YYYY-MM-DD format
      console.log(`✅ DOB populated for ${patient.first_name} ${patient.last_name}: ${patient.dob}`);
    } else {
      console.log(`⚠️ No valid DOB found for ${patient.first_name} ${patient.last_name}`);
    }
    
    setSearchData({
      patientId: patient.patient_id,
      patientName: `${patient.first_name} ${patient.last_name}`,
      patientDOB: formattedDOB
    });
    setIsPatientSelected(true);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  // Show toast notification
  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    // Auto-hide toast after 4 seconds
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    
    if (!selectedPatient) {
      showToast('Please select a patient first', 'error');
      return;
    }

    if (!searchData.patientDOB) {
      showToast('Please enter the patient\'s date of birth', 'error');
      return;
    }

    // Check if DOB matches
    if (searchData.patientDOB === selectedPatient.dob) {
      // Navigate to patient medical details page
      navigate(`/admin/patientfiles/${selectedPatient.patient_id}/medical_details`);
    } else {
      showToast('DOB does not match for the selected patient. Please verify and try again.', 'error');
    }
  };

  // Handle new patient button
  const handleNewPatient = () => {
    setSearchData({
      patientId: '',
      patientName: '',
      patientDOB: ''
    });
    setSelectedPatient(null);
    setIsPatientSelected(false);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  // Calculate age from DOB
  const calculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  // Handle edit button click
  const handleEditRecord = (section) => {
    setEditingSection(section);
    
    // Backup current data for cancel functionality
    let currentData = {};
    switch (section) {
      case 'general':
        currentData = { ...editablePatient };
        break;
      case 'medications':
        currentData = [...medications];
        setEditableMedications([...medications]);
        break;
      case 'medicalAllergies':
        const drugAllergies = allergies.filter(allergy => allergy.allergy_type === 'Drug');
        currentData = [...drugAllergies];
        setEditableMedicalAllergies([...drugAllergies]);
        break;
      case 'otherAllergies':
        const otherAllergies = allergies.filter(allergy => allergy.allergy_type !== 'Drug');
        currentData = [...otherAllergies];
        setEditableOtherAllergies([...otherAllergies]);
        break;
      default:
        break;
    }
    setBackupData(currentData);
  };

  // Handle save changes with proper database operations
  const handleSaveChanges = async (section) => {
    try {
      console.log(`💾 Saving changes for section: ${section}`);
      
      if (section === 'general') {
        // Save height and weight to database
        const token = localStorage.getItem('authToken');
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
        
        const updateData = {
          height: editablePatient.height || patient.height,
          weight: editablePatient.weight || patient.weight
        };
        
        console.log('💾 Saving height/weight to database:', updateData);
        
        const response = await fetch(`${apiBaseUrl}/patients/${patient.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });
        
        if (response.ok) {
          const updatedPatient = await response.json();
          console.log('✅ Height/weight saved successfully:', updatedPatient);
          
          // Update local state with fresh data from API
          setPatient(updatedPatient);
          setEditablePatient({
            height: updatedPatient.height || '',
            weight: updatedPatient.weight || ''
          });
          
          setEditingSection(null);
          showToast('Medical details saved successfully', 'success');
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ Failed to save height/weight:', response.status, errorData);
          throw new Error(`Failed to save: ${errorData.detail || 'Unknown error'}`);
        }
        return;
      }

      // Handle medical data sections
      if (section === 'medications') {
        await saveMedicationChanges();
      } else if (section === 'medicalAllergies') {
        await saveAllergyChanges('Drug', editableMedicalAllergies, originalMedicalAllergies);
      } else if (section === 'otherAllergies') {
        await saveAllergyChanges('Other', editableOtherAllergies, originalOtherAllergies);
      }

      // Clear inputs and exit editing mode
      setMedicationInput('');
      setMedicalAllergyInput('');
      setOtherAllergyInput('');
      setEditingSection(null);
      
      // Refresh data from API
      await fetchMedicalData(patient.id);
      
      showToast('Changes saved successfully', 'success');
    } catch (error) {
      console.error('❌ Error saving changes:', error);
      showToast('Failed to save changes', 'error');
    }
  };

  // Save medication changes
  const saveMedicationChanges = async () => {
    const originalIds = originalMedications.map(med => med.id);
    const editableIds = editableMedications.map(med => med.id).filter(id => id);

    // Find removed medications - mark as inactive instead of deleting
    const removedIds = originalIds.filter(id => !editableIds.includes(id));
    
    // Mark removed medications as inactive
    for (const medicationId of removedIds) {
      await patientMedicalService.updateMedication(medicationId, { status: 'Inactive' });
      console.log(`🔄 Marked medication ID ${medicationId} as Inactive`);
    }

    // Handle new medications (those without IDs)
    const newMedications = editableMedications.filter(med => !med.id);
    for (const medication of newMedications) {
      await patientMedicalService.createMedication({
        patient_id: patient.id,
        medication_name: medication.medication_name,
        dosage: medication.dosage,
        frequency: medication.frequency,
        start_date: new Date().toISOString().split('T')[0],
        status: 'Active'
      });
      console.log(`➕ Created new medication: ${medication.medication_name}`);
    }
  };

  // Save allergy changes
  const saveAllergyChanges = async (allergyType, editableAllergies, originalAllergies) => {
    const originalIds = originalAllergies.map(allergy => allergy.id);
    const editableIds = editableAllergies.map(allergy => allergy.id).filter(id => id);

    // Find removed allergies - mark as inactive instead of deleting
    const removedIds = originalIds.filter(id => !editableIds.includes(id));
    
    // Mark removed allergies as inactive
    for (const allergyId of removedIds) {
      await patientMedicalService.updateAllergy(allergyId, { status: 'Inactive' });
      console.log(`🔄 Marked allergy ID ${allergyId} as Inactive`);
    }

    // Handle new allergies (those without IDs)
    const newAllergies = editableAllergies.filter(allergy => !allergy.id);
    for (const allergy of newAllergies) {
      await patientMedicalService.createAllergy({
        patient_id: patient.id,
        allergen_name: allergy.allergen_name,
        allergy_type: allergyType === 'Drug' ? 'Drug' : (allergy.allergy_type || 'Other'),
        severity: allergy.severity || 'Mild',
        status: 'Active'
      });
      console.log(`➕ Created new allergy: ${allergy.allergen_name}`);
    }
  };

  // Handle cancel changes
  const handleCancelChanges = (section) => {
    setEditingSection(null);
    
    // Restore backup data
    switch (section) {
      case 'general':
        setEditablePatient(backupData);
        break;
      case 'medications':
        setEditableMedications([...backupData]);
        setMedicationInput('');
        break;
      case 'medicalAllergies':
        setEditableMedicalAllergies([...backupData]);
        setMedicalAllergyInput('');
        break;
      case 'otherAllergies':
        setEditableOtherAllergies([...backupData]);
        setOtherAllergyInput('');
        break;
      default:
        break;
    }
  };

  // Handle input change for editable fields
  const handleInputChange = (field, value) => {
    setEditablePatient(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle adding new tag
  const handleAddTag = (type, value) => {
    if (!value.trim()) return;
    
    switch (type) {
      case 'medications':
        // Parse medication input (e.g., "Aspirin 81mg daily")
        const parts = value.trim().split(' ');
        const medicationName = parts[0] || value.trim();
        const dosage = parts[1] || '';
        const frequency = parts.slice(2).join(' ') || '';
        
        const newMedication = {
          medication_name: medicationName,
          dosage: dosage,
          frequency: frequency,
          status: 'Active'
        };
        
        setEditableMedications(prev => [...prev, newMedication]);
        setMedicationInput('');
        break;
      case 'medicalAllergies':
        const newDrugAllergy = {
          allergen_name: value.trim(),
          allergy_type: 'Drug',
          severity: 'Mild',
          status: 'Active'
        };
        setEditableMedicalAllergies(prev => [...prev, newDrugAllergy]);
        setMedicalAllergyInput('');
        break;
      case 'otherAllergies':
        const newOtherAllergy = {
          allergen_name: value.trim(),
          allergy_type: 'Other',
          severity: 'Mild',
          status: 'Active'
        };
        setEditableOtherAllergies(prev => [...prev, newOtherAllergy]);
        setOtherAllergyInput('');
        break;
      default:
        break;
    }
  };

  // Handle removing tag
  const handleRemoveTag = (type, index) => {
    switch (type) {
      case 'medications':
        setEditableMedications(prev => prev.filter((_, i) => i !== index));
        break;
      case 'medicalAllergies':
        setEditableMedicalAllergies(prev => prev.filter((_, i) => i !== index));
        break;
      case 'otherAllergies':
        setEditableOtherAllergies(prev => prev.filter((_, i) => i !== index));
        break;
      default:
        break;
    }
  };

  // Handle key press for tag input
  const handleTagInputKeyPress = (e, type) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      switch (type) {
        case 'medications':
          handleAddTag('medications', medicationInput);
          break;
        case 'medicalAllergies':
          handleAddTag('medicalAllergies', medicalAllergyInput);
          break;
        case 'otherAllergies':
          handleAddTag('otherAllergies', otherAllergyInput);
          break;
        default:
          break;
      }
    }
  };

  // Calculate BMI from height and weight
  const calculateBMI = (height, weight) => {
    if (!height || !weight) return '';
    
    // Extract numeric values from strings (remove units like 'cm', 'cms', 'kg', 'kgs')
    const heightNum = parseFloat(height.toString().replace(/[^\d.]/g, ''));
    const weightNum = parseFloat(weight.toString().replace(/[^\d.]/g, ''));
    
    // Validate inputs
    if (isNaN(heightNum) || isNaN(weightNum) || heightNum <= 0 || weightNum <= 0) {
      return '';
    }
    
    // Convert height from cm to meters
    const heightInMeters = heightNum / 100;
    
    // Calculate BMI = weight (kg) / height (m)²
    const bmi = weightNum / (heightInMeters * heightInMeters);
    
    // Return BMI rounded to 1 decimal place
    return bmi.toFixed(1);
  };

  // Dummy medical data for demonstration
  const dummyMedications = [
    "Lisinopril 10mg daily",
    "Metformin 500mg twice daily", 
    "Atorvastatin 20mg nightly",
    "Aspirin 81mg daily"
  ];

  const dummyMedicalAllergies = [
    "Penicillin",
    "Sulfa drugs",
    "Latex",
    "Iodine contrast"
  ];

  const dummyOtherAllergies = [
    "Pollen",
    "Dust mites", 
    "Cat dander",
    "Shellfish"
  ];

  // BMI categorization function with color codes and notes
  const getBMICategory = (bmi) => {
    const bmiValue = parseFloat(bmi);
    if (isNaN(bmiValue)) return null;

    if (bmiValue < 16.0) {
      return {
        category: "Severe Underweight",
        color: "red",
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        borderColor: "border-red-200",
        dotColor: "bg-red-500",
        note: "Very high risk of malnutrition, poor physical performance, fatigue, and even serious health outcomes."
      };
    } else if (bmiValue >= 16.0 && bmiValue <= 16.9) {
      return {
        category: "Moderate Underweight",
        color: "orange",
        bgColor: "bg-orange-100",
        textColor: "text-orange-800",
        borderColor: "border-orange-200",
        dotColor: "bg-orange-500",
        note: "Still a significant health concern—may indicate nutrient deficiency or underlying illnesses."
      };
    } else if (bmiValue >= 17.0 && bmiValue <= 18.4) {
      return {
        category: "Mild Underweight",
        color: "orange",
        bgColor: "bg-orange-100",
        textColor: "text-orange-800",
        borderColor: "border-orange-200",
        dotColor: "bg-orange-500",
        note: "Borderline low and may benefit from nutritional monitoring."
      };
    } else if (bmiValue >= 18.5 && bmiValue <= 24.9) {
      return {
        category: "Normal / Healthy Weight",
        color: "green",
        bgColor: "bg-green-100",
        textColor: "text-green-800",
        borderColor: "border-green-200",
        dotColor: "bg-green-500",
        note: "Associated with the lowest overall health risk on a population level."
      };
    } else if (bmiValue >= 25.0 && bmiValue <= 29.9) {
      return {
        category: "Overweight (Pre‑obese)",
        color: "orange",
        bgColor: "bg-orange-100",
        textColor: "text-orange-800",
        borderColor: "border-orange-200",
        dotColor: "bg-orange-500",
        note: "Elevated risk of chronic diseases like type 2 diabetes, hypertension, and cardiovascular conditions."
      };
    } else if (bmiValue >= 30.0 && bmiValue <= 34.9) {
      return {
        category: "Obese (Class I)",
        color: "red",
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        borderColor: "border-red-200",
        dotColor: "bg-red-500",
        note: "Moderate obesity; increased risk of multiple health issues—early lifestyle changes are strongly recommended."
      };
    } else if (bmiValue >= 35.0 && bmiValue <= 39.9) {
      return {
        category: "Obese (Class II)",
        color: "red",
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        borderColor: "border-red-200",
        dotColor: "bg-red-500",
        note: "Severe obesity; substantially elevated health risks, often requiring more intensive management."
      };
    } else if (bmiValue >= 40.0) {
      return {
        category: "Obese (Class III / Severe Obesity)",
        color: "red",
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        borderColor: "border-red-200",
        dotColor: "bg-red-500",
        note: "Very severe obesity; associated with the highest likelihood of serious comorbidities and typically needs clinical intervention."
      };
    }
    
    return null;
  };

  // Component to render tags for multiple values
  const renderTags = (items, maxDisplay = 2) => {
    if (!items || items.length === 0) return null;
    
    const displayItems = items.slice(0, maxDisplay);
    const remainingCount = items.length - maxDisplay;
    
    return (
      <div className="flex flex-wrap gap-1 p-2">
        {displayItems.map((item, index) => (
          <span
            key={index}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
          >
            {item}
          </span>
        ))}
        {remainingCount > 0 && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
            +{remainingCount} more
          </span>
        )}
      </div>
    );
  };

    // Function to render editable tags with add/remove functionality
  const renderEditableTags = (items, type, inputValue, setInputValue) => {
    const formatItemDisplay = (item, type) => {
      if (type === 'medications') {
        return `${item.medication_name || ''} ${item.dosage || ''} ${item.frequency || ''}`.trim();
      } else if (type === 'medicalAllergies' || type === 'otherAllergies') {
        const severityDisplay = item.severity ? ` (${item.severity})` : '';
        return `${item.allergen_name || ''}${severityDisplay}`;
      }
      return item.toString();
    };

    const getItemBgColor = (item, type) => {
      if (type === 'medicalAllergies' || type === 'otherAllergies') {
        const severity = item.severity;
        if (severity === 'Severe' || severity === 'Life-threatening') {
          return 'bg-red-100 text-red-800 border-red-200';
        } else if (severity === 'Moderate') {
          return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        }
      }
      return 'bg-blue-100 text-blue-800 border-blue-200';
    };

    return (
      <div className="w-full bg-white border border-gray-300 rounded-md min-h-[2.5rem] p-2 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
        <div className="flex flex-wrap gap-1 items-center">
          {items.map((item, index) => (
            <span
              key={index}
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border group ${getItemBgColor(item, type)}`}
            >
              {formatItemDisplay(item, type)}
              <button
                onClick={() => handleRemoveTag(type, index)}
                className="ml-1 w-3 h-3 rounded-full bg-red-200 hover:bg-red-300 flex items-center justify-center opacity-70 hover:opacity-100 transition-all"
                title="Remove"
              >
                <svg className="w-2 h-2 text-red-800" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </span>
          ))}
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => handleTagInputKeyPress(e, type)}
            placeholder={items.length === 0 ? "Type and press Enter to add..." : "Add more..."}
            className="flex-1 min-w-[120px] outline-none text-sm placeholder-gray-400 bg-transparent"
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">Loading patient medical details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Patient Not Found</h2>
          <p className="text-gray-600 mb-4">The patient with ID {patientId} could not be found.</p>
          <Link
            to="/admin/patientfiles"
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Patient Files
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Top Banner */}
      <div className="bg-blue-600 text-white px-6 py-1.5 rounded-lg mb-6">
        <h1 className="text-lg font-semibold text-center">Patient Files</h1>
      </div>

      {/* White Content Container with Search Block */}
      <div className={`bg-white rounded-lg shadow-md mb-4 ${isSearchExpanded ? "p-4" : "p-3"}`}>
        {/* Search Section */}
        <div className={isSearchExpanded ? "mb-4" : "mb-2"}>
          {/* Search Header with Icon and Toggle */}
          <div className={`flex items-center justify-between ${isSearchExpanded ? "mb-2" : "mb-0"}`}>
            <div className="flex items-center space-x-2">
              <Search className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-800">Patient Search</h2>
            </div>
            <button
              type="button"
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className={`bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center transition-colors shadow-sm ${isSearchExpanded ? "w-8 h-8" : "w-7 h-7"}`}
            >
              {isSearchExpanded ? (
                <Minus className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {/* Collapsible Content */}
          {isSearchExpanded && (
            <div>
              {/* Blue stylish divider */}
              <div className="h-0.5 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mb-4"></div>
              
              {/* Search Form */}
            <form onSubmit={handleSearch} className="space-y-2">
              {/* Input Fields and Buttons Row - All in One Line */}
              <div className="flex items-end space-x-3">
                {/* Patient ID Input */}
                <div className="flex-1 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID:</label>
                  <input
                    type="text"
                    placeholder="Enter Patient ID"
                    value={searchData.patientId}
                    onChange={(e) => handlePatientIdChange(e.target.value)}
                    onFocus={() => setShowPatientIdDropdown(true)}
                    disabled={isPatientSelected}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                  />
                  
                  {/* Dropdown for patient selection by ID */}
                  {showPatientIdDropdown && filteredPatientsById.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                      {filteredPatientsById.map((patient) => (
                        <div
                          key={patient.id || patient.patient_id}
                          onClick={() => handlePatientSelect(patient)}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                        >
                          <div className="font-medium text-gray-900">{patient.patient_id}</div>
                          <div className="text-sm text-gray-600">{patient.first_name} {patient.last_name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Patient Name Input */}
                <div className="flex-1 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                  <input
                    type="text"
                    placeholder="Enter Patient Name"
                    value={searchData.patientName}
                    onChange={(e) => handlePatientNameChange(e.target.value)}
                    onFocus={() => setShowPatientNameDropdown(true)}
                    disabled={isPatientSelected}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                  />
                  
                  {/* Dropdown for patient selection by name */}
                  {showPatientNameDropdown && filteredPatientsByName.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                      {filteredPatientsByName.map((patient) => (
                        <div
                          key={patient.id || patient.patient_id}
                          onClick={() => handlePatientSelect(patient)}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                        >
                          <div className="font-medium text-gray-900">{patient.first_name} {patient.last_name}</div>
                          <div className="text-sm text-gray-500">ID: {patient.patient_id}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Patient DOB Input */}
                <div className="w-48">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient DOB:</label>
                  <input
                    type="date"
                    value={searchData.patientDOB}
                    onChange={(e) => setSearchData(prev => ({ ...prev, patientDOB: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                  />
                </div>
                
                {/* Search Button */}
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md transition-colors duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
                >
                  <Search className="h-3 w-3" />
                  <span>Search</span>
                </button>
                
                {/* Reset Fields Button */}
                <button
                  type="button"
                  onClick={handleNewPatient}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md transition-colors duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
                >
                <RotateCcw className="h-3 w-3" />
                <span>Reset Fields</span>
              </button>
              </div>
            </form>
            </div>
          )}
        </div>
      </div>

      {/* Patient Name Header */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg mb-6 shadow-sm relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-200 rounded-full -translate-y-12 translate-x-12"></div>
          <div className="absolute bottom-0 right-0 w-16 h-16 bg-blue-300 rounded-full translate-y-8 translate-x-8"></div>
        </div>
        
        {/* Patient name with enhanced styling */}
        <div className="relative z-10">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-5 bg-blue-500 rounded-full"></div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide leading-tight">
                {patient.first_name} {patient.last_name}
              </h2>
              <div className="h-0.5 w-12 bg-blue-500 mt-1 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Left Navigation Sidebar */}
      <div className="flex gap-6">
        <div className="w-64">
          <div className="bg-white rounded-lg shadow-md p-4">
            <nav className="space-y-2">
              <Link 
                to={`/admin/patientfiles/${patientId}/bookings`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Bookings
              </Link>
              <Link 
                to={`/admin/patientfiles/${patientId}/general_details`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                General Details
              </Link>
              <button className="w-full text-left px-3 py-2 rounded-md bg-blue-600 text-white transition-colors">
                Medical Details
              </button>
              <Link 
                to={`/admin/patientfiles/${patientId}/history`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                History
              </Link>
              <Link 
                to={`/admin/patientfiles/${patientId}/analytics`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Analytics
              </Link>
              <Link 
                to={`/admin/patientfiles/${patientId}/documents`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Documents
              </Link>
            </nav>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 space-y-6">
          {/* General Details Panel */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <User className="h-5 w-5" />
                  <h4 className="font-semibold">General Details</h4>
                </div>
                <button
                  type="button"
                  onClick={() => setIsGeneralDetailsExpanded(!isGeneralDetailsExpanded)}
                  className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
                >
                  {isGeneralDetailsExpanded ? (
                    <Minus className="h-3 w-3" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
            {isGeneralDetailsExpanded && (
            <div className="p-4">
              {/* Single row layout for Patient Height, Weight, BMI */}
              <div className="flex gap-3">
                {/* Patient Height */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Height</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={editingSection === 'general' ? editablePatient.height : (patient.height || 'Patient Height')}
                      onChange={(e) => handleInputChange('height', e.target.value)}
                      readOnly={editingSection !== 'general'}
                      className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 pr-12 ${
                        editingSection === 'general' ? 'bg-white' : 'bg-gray-50'
                      }`}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center">
                      <div className="h-full w-px bg-gray-300 mx-2"></div>
                      <span className="text-xs text-gray-500 font-medium pr-3">cms</span>
                    </div>
                  </div>
                </div>

                {/* Patient Weight */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Weight</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={editingSection === 'general' ? editablePatient.weight : (patient.weight || 'Patient Weight')}
                      onChange={(e) => handleInputChange('weight', e.target.value)}
                      readOnly={editingSection !== 'general'}
                      className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 pr-12 ${
                        editingSection === 'general' ? 'bg-white' : 'bg-gray-50'
                      }`}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center">
                      <div className="h-full w-px bg-gray-300 mx-2"></div>
                      <span className="text-xs text-gray-500 font-medium pr-3">kgs</span>
                    </div>
                  </div>
                </div>

                {/* Patient BMI */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient BMI</label>
                  <div className="space-y-2">
                    <div className="relative">
                                          <input
                      type="text"
                      value={calculateBMI(
                        editingSection === 'general' ? editablePatient.height : patient.height, 
                        editingSection === 'general' ? editablePatient.weight : patient.weight
                      ) || 'BMI will calculate automatically'}
                      readOnly
                      className="w-full px-3 py-1 bg-gray-50 border border-gray-300 rounded-md text-gray-900 pr-12"
                    />
                    {calculateBMI(
                      editingSection === 'general' ? editablePatient.height : patient.height, 
                      editingSection === 'general' ? editablePatient.weight : patient.weight
                    ) && (
                        <div className="absolute inset-y-0 right-0 flex items-center">
                          <div className="h-full w-px bg-gray-300 mx-2"></div>
                          <span className="text-xs text-gray-500 font-medium pr-3">kg/m²</span>
                        </div>
                      )}
                    </div>
                    
                    {/* BMI Category Display */}
                    {calculateBMI(
                      editingSection === 'general' ? editablePatient.height : patient.height, 
                      editingSection === 'general' ? editablePatient.weight : patient.weight
                    ) && getBMICategory(calculateBMI(
                      editingSection === 'general' ? editablePatient.height : patient.height, 
                      editingSection === 'general' ? editablePatient.weight : patient.weight
                    )) && (
                      <div className="relative">
                        <div 
                          className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all duration-200 hover:shadow-md ${
                            getBMICategory(calculateBMI(
                              editingSection === 'general' ? editablePatient.height : patient.height, 
                              editingSection === 'general' ? editablePatient.weight : patient.weight
                            )).bgColor
                          } ${
                            getBMICategory(calculateBMI(
                              editingSection === 'general' ? editablePatient.height : patient.height, 
                              editingSection === 'general' ? editablePatient.weight : patient.weight
                            )).textColor
                          } ${
                            getBMICategory(calculateBMI(
                              editingSection === 'general' ? editablePatient.height : patient.height, 
                              editingSection === 'general' ? editablePatient.weight : patient.weight
                            )).borderColor
                          }`}
                          onMouseEnter={() => setShowBMITooltip(true)}
                          onMouseLeave={() => setShowBMITooltip(false)}
                        >
                          <div className={`w-2 h-2 rounded-full mr-2 ${getBMICategory(calculateBMI(
                            editingSection === 'general' ? editablePatient.height : patient.height, 
                            editingSection === 'general' ? editablePatient.weight : patient.weight
                          )).dotColor}`}></div>
                          <span className="font-semibold">{getBMICategory(calculateBMI(
                            editingSection === 'general' ? editablePatient.height : patient.height, 
                            editingSection === 'general' ? editablePatient.weight : patient.weight
                          )).category}</span>
                          <svg className="w-3 h-3 ml-1 opacity-60" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                          </svg>
                        </div>
                        
                        {/* Tooltip */}
                        {showBMITooltip && (
                          <div className="absolute z-50 bottom-full left-0 mb-2 w-80 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl border border-gray-700">
                            <div className="font-semibold mb-1">
                              {getBMICategory(calculateBMI(
                                editingSection === 'general' ? editablePatient.height : patient.height, 
                                editingSection === 'general' ? editablePatient.weight : patient.weight
                              )).category}
                            </div>
                            <div className="text-gray-300 leading-relaxed">
                              {getBMICategory(calculateBMI(
                                editingSection === 'general' ? editablePatient.height : patient.height, 
                                editingSection === 'general' ? editablePatient.weight : patient.weight
                              )).note}
                            </div>
                            {/* Tooltip arrow */}
                            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-4 space-x-2">
                {editingSection !== 'general' ? (
                  <button 
                    onClick={() => handleEditRecord('general')}
                    className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Record</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleCancelChanges('general')}
                      className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <X className="h-4 w-4" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={() => handleSaveChanges('general')}
                      className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </button>
                  </>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Medications Panel */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Pill className="h-5 w-5" />
                  <h4 className="font-semibold">Medications</h4>
                </div>
                <button
                  type="button"
                  onClick={() => setIsMedicationsExpanded(!isMedicationsExpanded)}
                  className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
                >
                  {isMedicationsExpanded ? (
                    <Minus className="h-3 w-3" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
            {isMedicationsExpanded && (
            <div className="p-4">
              {/* Single row layout for Medications */}
              <div className="flex gap-3">
                {/* Patient Medications */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Medications</label>
                  {editingSection === 'medications' ? (
                    renderEditableTags(editableMedications, 'medications', medicationInput, setMedicationInput)
                  ) : medicalDataLoading ? (
                    <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center justify-center">
                      <div className="flex items-center space-x-2 text-gray-500">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm">Loading medications...</span>
                      </div>
                    </div>
                                    ) : (
                    <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center">
                      {medications.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5 p-2">
                          {medications.map((med, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                            >
                              {med.medication_name} {med.dosage} - {med.frequency}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm p-2">No medications found</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end mt-4 space-x-2">
                {editingSection !== 'medications' ? (
                  <button 
                    onClick={() => handleEditRecord('medications')}
                    className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Record</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleCancelChanges('medications')}
                      className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <X className="h-4 w-4" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={() => handleSaveChanges('medications')}
                      className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </button>
                  </>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Medical Allergies Panel */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-5 w-5" />
                  <h4 className="font-semibold">Medical Allergies</h4>
                </div>
                <button
                  type="button"
                  onClick={() => setIsMedicalAllergiesExpanded(!isMedicalAllergiesExpanded)}
                  className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
                >
                  {isMedicalAllergiesExpanded ? (
                    <Minus className="h-3 w-3" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
            {isMedicalAllergiesExpanded && (
            <div className="p-4">
              {/* Single row layout for Medical Allergies */}
              <div className="flex gap-3">
                {/* Patient Medical Allergies */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Medical Allergies</label>
                  {editingSection === 'medicalAllergies' ? (
                    renderEditableTags(editableMedicalAllergies, 'medicalAllergies', medicalAllergyInput, setMedicalAllergyInput)
                  ) : medicalDataLoading ? (
                    <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center justify-center">
                      <div className="flex items-center space-x-2 text-gray-500">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm">Loading allergies...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center">
                      {allergies.filter(allergy => allergy.allergy_type === 'Drug').length > 0 ? (
                        <div className="flex flex-wrap gap-1.5 p-2">
                          {allergies
                            .filter(allergy => allergy.allergy_type === 'Drug')
                            .map((allergy, index) => (
                              <span
                                key={index}
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                                  allergy.severity === 'Severe' || allergy.severity === 'Life-threatening'
                                    ? 'bg-red-100 text-red-800 border-red-200'
                                    : allergy.severity === 'Moderate'
                                    ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                    : 'bg-orange-100 text-orange-800 border-orange-200'
                                }`}
                              >
                                {allergy.allergen_name}
                                <span className="ml-1 text-xs">({allergy.severity})</span>
                              </span>
                            ))}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm p-2">No drug allergies found</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end mt-4 space-x-2">
                {editingSection !== 'medicalAllergies' ? (
                  <button 
                    onClick={() => handleEditRecord('medicalAllergies')}
                    className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Record</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleCancelChanges('medicalAllergies')}
                      className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <X className="h-4 w-4" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={() => handleSaveChanges('medicalAllergies')}
                      className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </button>
                  </>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Other Allergies Panel */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5" />
                  <h4 className="font-semibold">Other Allergies</h4>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOtherAllergiesExpanded(!isOtherAllergiesExpanded)}
                  className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
                >
                  {isOtherAllergiesExpanded ? (
                    <Minus className="h-3 w-3" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
            {isOtherAllergiesExpanded && (
            <div className="p-4">
              {/* Single row layout for Other Allergies */}
              <div className="flex gap-3">
                {/* Patient Other Allergies */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Other Allergies</label>
                  {editingSection === 'otherAllergies' ? (
                    renderEditableTags(editableOtherAllergies, 'otherAllergies', otherAllergyInput, setOtherAllergyInput)
                  ) : medicalDataLoading ? (
                    <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center justify-center">
                      <div className="flex items-center space-x-2 text-gray-500">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm">Loading allergies...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full space-y-2">
                      {/* Textarea Display for Other Allergies */}
                      {(() => {
                        const otherAllergies = allergies.filter(allergy => allergy.allergy_type !== 'Drug');
                        return otherAllergies.length > 0 ? (
                          <textarea
                            value={otherAllergies.map((allergy, index) => {
                              const typeDisplay = allergy.allergy_type ? ` (${allergy.allergy_type})` : '';
                              const severityDisplay = allergy.severity ? ` [${allergy.severity}]` : '';
                              const statusDisplay = allergy.status !== 'Active' ? ` [${allergy.status}]` : '';
                              return `${index + 1}. ${allergy.allergen_name || ''}${typeDisplay}${severityDisplay}${statusDisplay}`;
                            }).join('\n')}
                            readOnly
                            rows={Math.max(3, otherAllergies.length)}
                            className="w-full p-3 border border-gray-300 rounded-md text-sm font-mono bg-gray-50 resize-none focus:outline-none"
                          />
                        ) : (
                          <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center justify-center">
                            <span className="text-gray-500 text-sm">No other allergies found</span>
                          </div>
                        );
                      })()}

                      {/* Tag Display for Visual Representation */}
                      {(() => {
                        const otherAllergies = allergies.filter(allergy => allergy.allergy_type !== 'Drug');
                        return otherAllergies.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 p-2 bg-white border border-gray-200 rounded-md">
                            {otherAllergies.map((allergy, index) => {
                              const isInactive = allergy.status !== 'Active';
                              return (
                                <span
                                  key={index}
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                                    isInactive
                                      ? 'bg-gray-100 text-gray-600 border-gray-300'
                                      : allergy.severity === 'Severe' || allergy.severity === 'Life-threatening'
                                      ? 'bg-red-100 text-red-800 border-red-200'
                                      : allergy.severity === 'Moderate'
                                      ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                      : 'bg-green-100 text-green-800 border-green-200'
                                  }`}
                                >
                                  {allergy.allergen_name}
                                  <span className="ml-1 text-xs">
                                    ({allergy.allergy_type})
                                    {allergy.status !== 'Active' && ` [${allergy.status}]`}
                                  </span>
                                </span>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end mt-4 space-x-2">
                {editingSection !== 'otherAllergies' ? (
                  <button 
                    onClick={() => handleEditRecord('otherAllergies')}
                    className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Record</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleCancelChanges('otherAllergies')}
                      className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <X className="h-4 w-4" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={() => handleSaveChanges('otherAllergies')}
                      className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </button>
                  </>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 transition-all duration-300 transform ${
          toast.type === 'error' 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
        }`}>
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="font-medium">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:bg-black hover:bg-opacity-20 rounded-full p-1 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminPatientMedicalDetailsPage;
